import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Col, Form, Input, InputNumber, message, Modal, Row, Space, Switch, Tag
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { polishTypeApi } from './itemConfigApi';
import '../../../../styles/erpModal.css';

const initialForm = () => ({
  polishTypeCode: '',
  polishTypeName: '',
  description:    '',
  sortOrder:      null,
  isActive:       true
});

function unwrapApiError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function SectionTitle({ children }) {
  return (
    <div style={{
      margin: '2px 0 6px', borderBottom: '1px solid #f0f0f0', paddingBottom: 4,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: '#6b7280'
    }}>{children}</div>
  );
}

function InlineHint({ icon, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      fontSize: 11.5, color: '#6b7280', lineHeight: 1.5,
      margin: '-2px 0 8px'
    }}>
      {icon ? <span style={{ marginTop: 1 }}>{icon}</span> : null}
      <span>{children}</span>
    </div>
  );
}

export default function PolishTypeModal({ open, mode = 'add', record = null, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readOnly = isView;
  const isSystem = (isEdit || isView) && record?.isSystem === true;
  const codeLocked = readOnly || isSystem;

  useEffect(() => {
    if (!open) return;
    if (mode === 'add' || !record) {
      form.resetFields();
      form.setFieldsValue(initialForm());
    } else {
      form.setFieldsValue({
        polishTypeCode: record.polishTypeCode || '',
        polishTypeName: record.polishTypeName || '',
        description:    record.description || '',
        sortOrder:      Number.isFinite(record.sortOrder) ? record.sortOrder : null,
        isActive:       record.isActive !== false
      });
    }
  }, [open, mode, record, form]);

  const onSubmit = useCallback(async () => {
    if (readOnly) return onClose?.();
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) { return; }
    setBusy(true);
    try {
      if (isEdit && record?.id) {
        const payload = {
          polishTypeName: values.polishTypeName,
          description:    values.description || null,
          sortOrder:      values.sortOrder,
          isActive:       values.isActive !== false
        };
        if (!isSystem) payload.polishTypeCode = values.polishTypeCode;
        await polishTypeApi.update(record.id, payload);
        message.success('Polish type updated.');
      } else {
        await polishTypeApi.create({
          polishTypeCode: String(values.polishTypeCode || '').trim().toUpperCase(),
          polishTypeName: String(values.polishTypeName || '').trim(),
          description:    values.description || null,
          sortOrder:      values.sortOrder,
          isActive:       values.isActive !== false
        });
        message.success('Polish type created.');
      }
      onSaved?.();
    } catch (e) {
      message.error(unwrapApiError(e, isEdit ? 'Failed to update polish type.' : 'Failed to create polish type.'));
    } finally {
      setBusy(false);
    }
  }, [form, isEdit, readOnly, isSystem, record, onClose, onSaved]);

  const titleNode = useMemo(() => {
    const label = mode === 'add' ? 'New Polish Type' : `Polish Type — ${record?.polishTypeCode || ''}`;
    const chips = [];
    if (record?.isSystem) chips.push(<Tag key="sys" icon={<LockOutlined />} color="default" style={{ marginInlineEnd: 0 }}>System</Tag>);
    if (record) chips.push(
      <Tag key="active" color={record.isActive === false ? 'default' : 'success'} style={{ marginInlineEnd: 0 }}>
        {record.isActive === false ? 'Inactive' : 'Active'}
      </Tag>);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 28 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{label}</span>
        {chips.length ? <Space size={6} wrap>{chips}</Space> : null}
      </div>
    );
  }, [mode, record]);

  return (
    <Modal
      open={open}
      title={titleNode}
      onCancel={onClose}
      onOk={readOnly ? onClose : onSubmit}
      okText={readOnly ? 'Close' : isEdit ? 'Save' : 'Create'}
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      width={680}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-enterprise-modal"
    >
      {isSystem ? (
        <InlineHint icon={<LockOutlined style={{ color: '#6b7280' }} />}>
          Seeded system polish. Code is locked; you can rename, edit the
          description, sort order, and active flag.
        </InlineHint>
      ) : null}

      <Form form={form} layout="vertical" size="middle" requiredMark="optional"
        disabled={readOnly} className="item-config-compact-form erp-enterprise-form">
        <style>{`
          .item-config-compact-form .ant-form-item { margin-bottom: 10px; }
          .item-config-compact-form .ant-form-item-label { padding-bottom: 2px; }
          .item-config-compact-form .ant-form-item-label > label { font-size: 12px; color: #475569; height: 22px; }
          .item-config-compact-form .ant-input,
          .item-config-compact-form .ant-input-number-input,
          .item-config-compact-form .ant-select-selector { font-size: 13px; }
          .item-config-compact-form .ant-form-item-explain { font-size: 11.5px; min-height: 16px; }
        `}</style>

        <SectionTitle>Basic</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Polish Code" name="polishTypeCode"
              rules={[
                { required: true, message: 'Required' },
                { max: 40, message: 'Max 40 chars' },
                { pattern: /^[A-Za-z0-9_-]+$/, message: 'A-Z, 0-9, _, -' }
              ]}>
              <Input
                maxLength={40}
                disabled={codeLocked}
                placeholder="e.g. CEP"
                suffix={codeLocked ? <LockOutlined style={{ color: '#9ca3af' }} /> : null}
                onChange={(e) => form.setFieldsValue({
                  polishTypeCode: e.target.value.toUpperCase()
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label="Polish Name" name="polishTypeName"
              rules={[
                { required: true, message: 'Required' },
                { max: 120, message: 'Max 120 chars' }
              ]}>
              <Input maxLength={120} placeholder="e.g. Cut Edge Polish" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Description" name="description">
              <Input.TextArea rows={2} maxLength={2000} placeholder="Optional explanation" />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>Control</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Sort Order" name="sortOrder">
              <InputNumber min={0} max={999999} precision={0}
                style={{ width: '100%' }} placeholder="e.g. 10" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
